import { OCEAN_STATUS_TOKENS } from './ocean-status-order.mjs?v=20260806.100';

const SAFE_ID = /^[A-Za-z0-9._:@-]{1,200}$/;
// The Ocean Statuses a Container can hold. One list, defined beside the order Alan
// gave them in, so the editor, the status filter, every legend built from this and
// the per-status colours all read in that order (Alan, 2026-08-09).
//
// Four older values are retired below: `not_arrived`, `arrived`, `arrived_at_port`
// and `available`. They survive in `STATUS_ALIASES` in
// container-status-presentation.mjs, so a record already carrying one still
// normalises and renders; none of the 452 production Containers does. They are
// simply no longer offered. `inspection_cleared` was on that list between
// 2026-08-09 and 2026-08-12 and is now back on the menu as 查验放行 — it is a
// status in its own right, not another spelling of one.
export const OCEAN_STATUS_VALUES = OCEAN_STATUS_TOKENS;
// The empty leg starts only once the cargo is out of the box. `customer_report_empty`
// is the action that opens after the loaded delivery completes, so a Container sits at
// `delivered_to_warehouse` while it is still being devanned — laden until the customer
// reports it empty. Laden is derived by subtraction so the two sets always partition the
// canonical list with no overlap and no gaps.
export const EMPTY_OCEAN_STATUS_VALUES = Object.freeze([
  'empty_return_reported', 'empty_returned',
]);
export const LADEN_OCEAN_STATUS_VALUES = Object.freeze(
  OCEAN_STATUS_VALUES.filter((value) => !EMPTY_OCEAN_STATUS_VALUES.includes(value)),
);
// What a human may pick and what the system will accept are two different lists.
//
// `OCEAN_STATUS_VALUES` above is the picker, and nothing else is offered anywhere in the UI. No
// workflow hardcodes a status — `clerk_port_intake` and friends validate whatever the form
// supplied — so narrowing the picker is enough to retire a value going forward.
//
// This set stays wider on purpose. A stored Container that still carries one of the retired
// aliases must survive being re-submitted, and `normalizeContainerStatus` keeps rendering it.
// Nothing can newly create one.
const RETIRED_OCEAN_STATUS_ALIASES = Object.freeze([
  'not_arrived', 'arrived', 'arrived_at_port', 'available',
]);
const OCEAN_STATUSES = new Set([...OCEAN_STATUS_VALUES, ...RETIRED_OCEAN_STATUS_ALIASES]);
const PAYMENT_STATUSES = new Set(['paid', 'unpaid']);
const REMITTANCE_STATUSES = new Set(['received', 'not_received']);
const CONTAINER_SIZES = new Set(['20', '40HQ', '45', 'Custom']);
const PRE_ALERT_STATUSES = new Set(['Pre-Alert', 'Pre-Alert + DO']);
const LINE_TYPES = new Set([
  'loaded_pickup_cost', 'empty_return_cost', 'transfer_cost',
  'driver_special_cost', 'container_exception_cost', 'customer_special_charge',
]);

export class WorkflowUiError extends Error {
  constructor(code, field = null) {
    super(code);
    this.name = 'WorkflowUiError';
    this.code = code;
    this.field = field;
  }
}

function fail(code, field = null) {
  throw new WorkflowUiError(code, field);
}

function text(value, field, max = 500, { optional = false } = {}) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized && optional) return null;
  if (!normalized || normalized.length > max) fail('REQUIRED_OR_INVALID', field);
  return normalized;
}

function safeId(value, field) {
  const normalized = text(value, field, 200);
  if (!SAFE_ID.test(normalized)) fail('REQUIRED_OR_INVALID', field);
  return normalized;
}

function dateIso(value, field) {
  const normalized = text(value, field, 100);
  const date = new Date(normalized);
  if (!Number.isFinite(date.getTime())) fail('REQUIRED_OR_INVALID', field);
  return date.toISOString();
}

// The server's own vocabulary for the chassis answer.
const CUSTOMER_CHASSIS_VALUES = new Set(['yes', 'no', 'own_chassis']);

// Empty is a legitimate answer for every field but the container number: it means
// nobody knows yet, which is the normal state of a Task booked over the phone.
function optionalEnum(value, allowed, field) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) return '';
  return enumValue(normalized, allowed, field);
}

function enumValue(value, allowed, field) {
  const normalized = text(value, field, 100);
  if (!allowed.has(normalized)) fail('REQUIRED_OR_INVALID', field);
  return normalized;
}

export function canonicalWorkflowRole(role) {
  return ['admin', 'dispatcher', 'clerk', 'finance', 'customer'].includes(role) ? role : '';
}

export function buildStatusCustomerIdentityPatch({ role, customer, linkedCustomer } = {}) {
  if (canonicalWorkflowRole(role) !== 'admin') return {};
  if (
    !customer?.ref
    || typeof customer.name !== 'string'
    || !customer.name.trim()
    || typeof linkedCustomer?.accountUid !== 'string'
    || !linkedCustomer.accountUid.trim()
    || typeof linkedCustomer?.accountEmail !== 'string'
    || !linkedCustomer.accountEmail.trim()
  ) {
    fail('REQUIRED_OR_INVALID', 'customer_id');
  }
  return {
    customer_ref: customer.ref,
    customer_name: customer.name,
    customer_email: linkedCustomer.accountEmail,
    customer_uid: linkedCustomer.accountUid,
  };
}

export function canOpenWorkflowAction(action, user) {
  if (!action || !user?.uid) return false;
  const role = canonicalWorkflowRole(user.role);
  if (role === 'admin') return true;
  if (canonicalWorkflowRole(action.responsible_role) !== role) return false;
  return !action.assigned_uid || action.assigned_uid === user.uid;
}

export function buildCustomerTaskRequest(raw, requestId) {
  const rows = Array.isArray(raw?.containers) ? raw.containers : [];
  if (rows.length < 1 || rows.length > 50) fail('ROW_COUNT_INVALID', 'containers');
  // The container number is the only thing a Task needs (Alan, 2026-08-10: "我需要
  // 有柜号就可以录入，剩下的可以更改"). Size, weight and Pre-Alert are booked later, in
  // the table, by whoever finds out.
  //
  // The server has always allowed this - normalizeSize, normalizeWeight and
  // normalizePreAlert in container-action-service.ts each return empty for an
  // absent value. The requirement lived only in the browser, so the form refused
  // work the backend was happy to take.
  //
  // customer_chassis travels with the row now. The form has always asked for it and
  // the server has always accepted it; the request builder simply never included
  // the key, so the answer was collected and thrown away.
  const containers = rows.map((row, index) => {
    const prefix = `containers.${index}`;
    const containerNo = text(row?.container_no, `${prefix}.container_no`, 32).toUpperCase();
    const weightValue = String(row?.weight ?? '').trim();
    const weight = weightValue === '' ? null : Number(weightValue);
    if (weight !== null && (!Number.isFinite(weight) || weight <= 0 || weight > 1_000_000)) {
      fail('REQUIRED_OR_INVALID', `${prefix}.weight`);
    }
    return {
      container_no: containerNo,
      size: optionalEnum(row?.size, CONTAINER_SIZES, `${prefix}.size`),
      weight,
      pre_alert: optionalEnum(row?.pre_alert, PRE_ALERT_STATUSES, `${prefix}.pre_alert`),
      customer_chassis: optionalEnum(
        row?.customer_chassis, CUSTOMER_CHASSIS_VALUES, `${prefix}.customer_chassis`,
      ),
    };
  });
  if (new Set(containers.map((row) => row.container_no)).size !== containers.length) {
    fail('DUPLICATE_CONTAINER_NO', 'containers');
  }
  return {
    request_id: safeId(requestId, 'request_id'),
    master_bl_no: text(raw?.master_bl_no, 'master_bl_no', 128, { optional: true }),
    do_ref: text(raw?.do_ref, 'do_ref', 500, { optional: true }),
    delivery_warehouse_address: text(
      raw?.delivery_warehouse_address,
      'delivery_warehouse_address',
      500,
      { optional: true },
    ) ?? '',
    containers,
  };
}

export function buildWorkflowActionValues(actionType, raw) {
  if (actionType === 'clerk_port_intake') {
    return {
      eta_at: dateIso(raw?.eta_at, 'eta_at'),
      ocean_status: enumValue(raw?.ocean_status, OCEAN_STATUSES, 'ocean_status'),
    };
  }
  if (actionType === 'clerk_lfd') {
    return {
      lfd_at: dateIso(raw?.lfd_at, 'lfd_at'),
      pickup_appointment_at: dateIso(raw?.pickup_appointment_at, 'pickup_appointment_at'),
      return_empty_lfd_at: dateIso(raw?.return_empty_lfd_at, 'return_empty_lfd_at'),
    };
  }
  if (actionType === 'finance_initial') {
    const customerPaid = raw?.customer_paid === true || raw?.customer_paid === 'true';
    if (!customerPaid) fail('CONFIRMATION_REQUIRED', 'customer_paid');
    return {
      ctf_status: enumValue(raw?.ctf_status, PAYMENT_STATUSES, 'ctf_status'),
      tmf_status: enumValue(raw?.tmf_status, PAYMENT_STATUSES, 'tmf_status'),
      customer_paid: true,
    };
  }
  if (actionType === 'customer_report_empty') {
    if (raw?.report_empty_confirmed !== true && raw?.report_empty_confirmed !== 'true') {
      fail('CONFIRMATION_REQUIRED', 'report_empty_confirmed');
    }
    return { report_empty_confirmed: true };
  }
  if (actionType === 'finance_completion') {
    const revision = Number(raw?.finance_summary_revision);
    if (!Number.isSafeInteger(revision) || revision < 1) {
      fail('FINANCE_SAVE_REQUIRED', 'finance_summary_revision');
    }
    return { finance_summary_revision: revision };
  }
  fail('DEDICATED_ACTION_REQUIRED', 'action_type');
}

export function dollarsToCents(value, field = 'amount') {
  const source = typeof value === 'number' ? value : String(value ?? '').trim();
  if (source === '') fail('REQUIRED_OR_INVALID', field);
  const normalized = Number(source);
  if (!Number.isFinite(normalized) || normalized < 0) fail('REQUIRED_OR_INVALID', field);
  const cents = Math.round(normalized * 100);
  if (!Number.isSafeInteger(cents)) fail('REQUIRED_OR_INVALID', field);
  return cents;
}

export function buildFinanceSummaryRequest(raw, context) {
  return {
    operation: 'update_summary',
    container_id: safeId(context?.containerId, 'container_id'),
    request_id: safeId(context?.requestId, 'request_id'),
    expected_revision: Number.isSafeInteger(context?.revision) ? context.revision : 0,
    customer_quote_cents: dollarsToCents(raw?.customer_quote, 'customer_quote'),
    customer_receivable_cents: dollarsToCents(raw?.customer_receivable, 'customer_receivable'),
    ctf_status: enumValue(raw?.ctf_status, PAYMENT_STATUSES, 'ctf_status'),
    tmf_status: enumValue(raw?.tmf_status, PAYMENT_STATUSES, 'tmf_status'),
    remittance_status: enumValue(raw?.remittance_status, REMITTANCE_STATUSES, 'remittance_status'),
  };
}

export function buildFinanceLineRequest(raw, context) {
  const operation = context?.lineItemId ? 'update_line_item' : 'add_line_item';
  const request = {
    operation,
    container_id: safeId(context?.containerId, 'container_id'),
    request_id: safeId(context?.requestId, 'request_id'),
    expected_summary_revision: Number(context?.summaryRevision),
    line_type: enumValue(raw?.line_type, LINE_TYPES, 'line_type'),
    category: text(raw?.category, 'category', 100),
    date: text(raw?.date, 'date', 10),
    name_or_payee: text(raw?.name_or_payee, 'name_or_payee', 200),
    amount_cents: dollarsToCents(raw?.amount, 'amount'),
    details: text(raw?.details, 'details', 2_000, { optional: true }) ?? '',
  };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(request.date)
    || !Number.isFinite(Date.parse(`${request.date}T00:00:00Z`))) {
    fail('REQUIRED_OR_INVALID', 'date');
  }
  if (!Number.isSafeInteger(request.expected_summary_revision)
    || request.expected_summary_revision < 0) {
    fail('REQUIRED_OR_INVALID', 'expected_summary_revision');
  }
  if (operation === 'update_line_item') {
    request.line_item_id = safeId(context.lineItemId, 'line_item_id');
    request.expected_line_revision = Number(context.lineRevision);
    if (!Number.isSafeInteger(request.expected_line_revision)
      || request.expected_line_revision < 1) {
      fail('REQUIRED_OR_INVALID', 'expected_line_revision');
    }
  }
  return request;
}

export function evidenceKindForLeg(legType) {
  if (['load_out_to_customer', 'load_out_to_yard'].includes(legType)) return 'pod';
  if (legType === 'return_empty') return 'empty_return';
  return null;
}

export function evidenceObjectPath(taskId, kind, token) {
  const id = safeId(taskId, 'task_id');
  if (!['pod', 'empty_return'].includes(kind)) fail('REQUIRED_OR_INVALID', 'evidence_kind');
  const safeToken = String(token ?? '').trim();
  if (!/^[A-Za-z0-9-]{8,80}$/.test(safeToken)) fail('REQUIRED_OR_INVALID', 'evidence_token');
  return `dispatch_tasks/${id}/proofs/${kind}__${safeToken}`;
}

export function callableErrorCode(error) {
  const detail = error?.details?.code ?? error?.customData?.details?.code;
  if (typeof detail === 'string' && detail) return detail;
  const code = typeof error?.code === 'string' ? error.code.split('/').at(-1) : '';
  return code || 'UNKNOWN';
}
